import type { ComponentProps } from 'react';
import React from 'react';
import Link from 'next/link';
import { toast } from 'react-toastify';
import Loader from '~/components/common/parts/loading/Loading';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { PATH as DEALS_PATH } from '~/pages/deals';
import Styles from './Page.module.scss';
import { DealCard } from '~/components/common/deals/show/deal_card/DealCard';
import { DealConstructionScheduleClientCard } from '~/components/common/deals/show/construction_card/DealConstructionScheduleClientCard';
import { DealMain } from '~/components/common/deals/show/main/DealMain';
import { useDeals } from '~/hooks/useDeals';
import { DealOriginVendorClientCard } from '~/components/common/deals/show/vendor_card/DealOriginVendorClientCard';
import { useMessages } from '~/hooks/deals/useMessages';
import { ChatPopContainer } from '~/components/common/chat/ChatPopContainer';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { DealRepairCard } from '~/components/common/deals/show/repair_card/DealRepairCard';
import { DealVendorNoteCard } from '~/components/common/deals/show/deal_card/DealVendorNoteCard';
import { postTemporaryUnread } from '~/utils/api/deals/Message';
import { isSuccess } from '~/utils/api/api_handler';
import useItandiAccount from '~/hooks/useItandiAccount';
import { UPDATE_TEMPORARY_UNREAD_MESSAGE } from '~/models/Message';
import { SERVER_ERROR_MESSAGE } from '~/constants/AppConst';
import { DealVisitDateClientCard } from '~/components/common/deals/show/visit_date_card/DealVisitDateClientCard';
import { DealInspectionCandidateDateCard } from '~/components/common/deals/show/inspection_candidate_date_card/DealInspectionCandidateDateCard';
import { ItandiAccountContext } from '~/models/ItandiAccount';

export const Page: React.FC = () => {
  const id = useIdQueryHandler();
  const { isOpen, handleClose, handleOpen } = useOpenHandler();
  const { data: deal, mutate: mutateDeal, isLoading } = useDeals().show(id);
  const { data: messages, mutate: mutateMessages } = useMessages().index(id);
  const { data: account } = useItandiAccount().currentAccount();
  const handleToggleChat = React.useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleClose, handleOpen]);
  const handleMutate = React.useCallback(async () => {
    await mutateDeal?.();
  }, [mutateDeal]);
  const handleMutateMessage = React.useCallback(async () => {
    await mutateMessages?.();
    await mutateDeal?.();
  }, [mutateDeal, mutateMessages]);
  if (isLoading || deal === null || account === null) {
    return <Loader isLoading />;
  }
  const handleTemporaryUnread = async (): Promise<void> => {
    const result = await postTemporaryUnread(deal.id);
    if (isSuccess(result)) {
      toast.success(UPDATE_TEMPORARY_UNREAD_MESSAGE);
      await mutateDeal?.();
      handleClose();
      return;
    }
    toast.error(SERVER_ERROR_MESSAGE);
  };
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  return (
    <ItandiAccountContext.Provider value={account}>
      <DefaultV2>
        <DealLayout deal={deal} isVendor={false} items={items} noBackground>
          <div className={Styles.Container}>
            <div className={Styles.LeftCol}>
              <DealOriginVendorClientCard handleToggleChat={handleToggleChat} />
              <DealVendorNoteCard deal={deal} />
              <DealCard
                deal={deal}
                handleMutate={handleMutate}
                isVendor={false}
              />
              {deal.dealType === 'repair' && (
                <DealRepairCard
                  companyType="client"
                  deal={deal}
                  handleMutate={handleMutate}
                />
              )}
              <DealVisitDateClientCard />
              <DealInspectionCandidateDateCard
                deal={deal}
                handleMutate={handleMutate}
              />
              <DealConstructionScheduleClientCard />
            </div>
            <DealMain deal={deal} isAdmin={account.isAdmin} isVendor={false} />
          </div>
        </DealLayout>
        {messages !== null && (
          <ChatPopContainer
            deal={deal}
            handleClose={handleClose}
            handleMutate={handleMutateMessage}
            handleTemporaryUnread={handleTemporaryUnread}
            isAdmin={account.isAdmin}
            isOpen={isOpen}
            messages={messages}
            speaker="maintenance_company"
          />
        )}
      </DefaultV2>
    </ItandiAccountContext.Provider>
  );
};
